import { Controller } from '@hotwired/stimulus'
import { TemperatureConverter } from '../temperature-converter'
import { LengthConverter } from '../length-converter'
import { WeightConverter } from '../weight-converter'
import { FractionConverter } from '../fraction-converter'
import { DecimalMillimeterConverter } from '../decimal-millimeter-converter'
import { AnimationManager } from '../animation-manager'

/**
 * Stimulus controller for conversion table feature
 * Thin orchestration layer that maps DOM elements to converter modules
 */
export default class extends Controller {
  static targets = [
    // Temperature targets
    'tempInput',
    'tempDirection',
    'tempResult',
    // Length targets
    'lengthUnit',
    'lengthInput',
    'lengthResults',
    // Weight targets
    'weightUnit',
    'weightInput',
    'weightResults',
    // Fraction targets
    'fractionNumerator',
    'fractionDenominator',
    'fractionResult',
    // Decimal/Millimeter targets
    'decimalInput',
    'decimalDirection',
    'decimalResult'
  ]

  connect() {
    this.initializeConverters()
    this.animationManager = new AnimationManager()
    this.animationManager.initialize()
  }

  /**
   * Initialize converter instances
   * @private
   */
  initializeConverters() {
    this.temperatureConverter = new TemperatureConverter()
    this.lengthConverter = new LengthConverter()
    this.weightConverter = new WeightConverter()
    this.fractionConverter = new FractionConverter()
    this.decimalMillimeterConverter = new DecimalMillimeterConverter()
  }

  // Temperature conversion action
  convertTemperature() {
    const inputValue = parseFloat(this.tempInputTarget.value)
    if (isNaN(inputValue)) {
      this.tempResultTarget.value = ''
      return
    }

    const direction = this.getSelectedRadioValue('tempDirection')
    let result

    if (direction === 'fToC') {
      result = this.temperatureConverter.fahrenheitToCelsius(inputValue)
    } else {
      result = this.temperatureConverter.celsiusToFahrenheit(inputValue)
    }

    this.tempResultTarget.value = result.toFixed(2)
  }

  // Length conversion action
  convertLength() {
    const inputValue = parseFloat(this.lengthInputTarget.value)
    const selectedUnit = this.getSelectedRadioValue('lengthUnit')

    if (isNaN(inputValue) || !selectedUnit) {
      this.lengthResultsTarget.innerHTML = ''
      return
    }

    const results = this.lengthConverter.convertToAll(inputValue, selectedUnit)
    this.lengthResultsTarget.innerHTML = this.buildLengthResultsHtml(results, selectedUnit)
  }

  /**
   * Build HTML for length conversion results
   * @private
   */
  buildLengthResultsHtml(results, excludeUnit) {
    const items = Object.entries(results)
      .filter(([unit]) => unit !== excludeUnit)
      .map(([unit, value]) => `
        <div class="result-item">
          <span class="result-item__label">${this.formatUnitLabel(unit)}</span>
          <span class="result-item__value">${value}</span>
        </div>
      `)
      .join('')
    
    return `<div class="results-grid">${items}</div>`
  }

  /**
   * Format unit labels for display
   * @private
   */
  formatUnitLabel(unit) {
    const labels = {
      inches: 'Inches',
      feet: 'Feet',
      yards: 'Yards',
      miles: 'Miles',
      mm: 'Millimeters',
      cm: 'Centimeters',
      meters: 'Meters',
      km: 'Kilometers',
      ounces: 'Ounces',
      pounds: 'Pounds',
      grams: 'Grams',
      kg: 'Kilograms',
      tons: 'US Tons'
    }
    return labels[unit] || unit.charAt(0).toUpperCase() + unit.slice(1)
  }

  // Weight conversion action
  convertWeight() {
    const inputValue = parseFloat(this.weightInputTarget.value)
    const selectedUnit = this.getSelectedRadioValue('weightUnit')

    if (isNaN(inputValue) || !selectedUnit) {
      this.weightResultsTarget.innerHTML = ''
      return
    }

    const results = this.weightConverter.convertToAll(inputValue, selectedUnit)
    this.weightResultsTarget.innerHTML = this.buildWeightResultsHtml(results, selectedUnit)
  }

  /**
   * Build HTML for weight conversion results
   * @private
   */
  buildWeightResultsHtml(results, excludeUnit) {
    const items = Object.entries(results)
      .filter(([unit]) => unit !== excludeUnit)
      .map(([unit, value]) => `
        <div class="result-item">
          <span class="result-item__label">${this.formatUnitLabel(unit)}</span>
          <span class="result-item__value">${value}</span>
        </div>
      `)
      .join('')
    
    return `<div class="results-grid">${items}</div>`
  }

  // Fraction conversion action
  convertFraction() {
    const numerator = parseFloat(this.fractionNumeratorTarget.value)
    const denominator = parseFloat(this.fractionDenominatorTarget.value)

    if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
      this.fractionResultTarget.value = ''
      return
    }

    const result = this.fractionConverter.divide(numerator, denominator)
    this.fractionResultTarget.value = result
  }

  // Decimal/Millimeter conversion action
  convertDecimalMm() {
    const inputValue = parseFloat(this.decimalInputTarget.value)
    if (isNaN(inputValue)) {
      this.decimalResultTarget.value = ''
      return
    }

    const direction = this.getSelectedRadioValue('decimalDirection')
    let result

    if (direction === 'decToMm') {
      result = this.decimalMillimeterConverter.inchesToMillimeters(inputValue)
    } else {
      result = this.decimalMillimeterConverter.millimetersToInches(inputValue)
    }

    this.decimalResultTarget.value = result
  }

  /**
   * Get selected radio button value by name
   * @private
   */
  getSelectedRadioValue(name) {
    const selected = document.querySelector(`input[name=${name}]:checked`)
    return selected ? selected.value : null
  }
}
